import React, { Fragment, useEffect, useState } from 'react'
import AnalysisChart from './AnalysisChart'
import { Listbox, Transition } from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline'
import { QuestionDetails } from './QuestionCard'
import { Questions, IRoleAssessmentMap } from '../../Models'
import { getQuestionAnalysis, getRoleAssessmentMap } from '../../api/masterdata.api'
import useApiToken from '../../hooks/useApiToken'

const QuestionAnalysis: React.FC<{ Question: Questions; QuestionDetails: QuestionDetails }> =
    ({ Question, QuestionDetails }) => {
        const token = useApiToken();
        const [selectedRole, setSelectedRole] = useState<string>();
        const [selectedAssessment, setSelectedAssessment] = useState<string>();
        const [selectedOverall, setSelectedOverall] = useState(true)
        const [roleAssessmentMap, setRoleAssessmentMap] = useState<IRoleAssessmentMap[]>([]);
        const [roles, setRoles] = useState<Set<{ roleId: string, roleName: string }>>(new Set());
        const [filterQuestionDetails, setFilterQuestionDetails] = useState<QuestionDetails>(QuestionDetails);
        useEffect(() => {
            if (!token) return;
            getRoleAssessmentMap(Question.id, token).then(data => {
                setRoleAssessmentMap(data)
                var tempMap = new Map<string, { roleId: string, roleName: string }>();
                data?.forEach((d: IRoleAssessmentMap) => {
                    const key = `${d.roleId}-${d.roleName}`;
                    tempMap.set(key, { roleId: d.roleId, roleName: d.roleName });
                });
                setRoles(new Set(tempMap.values()));
            })
        }, [Question.id, selectedRole, selectedAssessment, token])

        useEffect(() => {
            if (!token) return;
            getQuestionAnalysis(Question.id, token, selectedRole, selectedAssessment).then(data => {
                setFilterQuestionDetails({
                    questionGroup: null,
                    answeredCorrectly: data?.correctCandidateCount ?? 0,
                    answeredIncorrectly: data?.incorrectCandidateCount ?? 0,
                    answers: null,
                    unanswered: data?.notRespondedCount ?? 0,
                    accuracy: data?.accuracy ?? 0,
                });
            })
        }, [Question.id, selectedRole, selectedAssessment, token])

        return (
            <div>
                <ul className="text-sm font-medium text-center space-x-1 text-gray-500 rounded-lg shadow sm:flex dark:divide-gray-700 dark:text-gray-400">
                    <li className="w-full focus-within:z-10">
                        <div className="inline-block w-full bg-white hover:text-gray-700 hover:bg-gray-50 focus:ring-4 focus:ring-blue-300 focus:outline-none dark:hover:text-white dark:bg-darkbglight/70 dark:hover:bg-darkbglight/50">
                            <Listbox value={selectedRole} onChange={(e)=>{setSelectedRole(e); setSelectedAssessment(undefined)}}>
                                <div className="relative">
                                    <Listbox.Button className={"flex items-center justify-between w-full cursor-pointer rounded-lg bg-white p-3 text-left border focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm dark:bg-transparent "+(selectedRole?'border-blue-600':'border-gray-300 dark:border-gray-600')}>
                                        <span className="w-full max-w-48 block font-semibold truncate dark:text-gray-300">
                                            {roles && roles.size === 0 ? "No Jobs Mapped" : selectedRole ? Array.from(roles).find((e) => selectedRole === e.roleId)?.roleName : "Jobs"}
                                        </span>
                                        <span className="pointer-events-none flex items-center pl-2">
                                            <ChevronUpDownIcon
                                                className="h-5 w-5 text-gray-400"
                                                aria-hidden="true"
                                            />
                                        </span>
                                    </Listbox.Button>
                                    <Transition
                                        as={Fragment}
                                        leave="transition ease-in duration-100"
                                        leaveFrom="opacity-100"
                                        leaveTo="opacity-0"
                                    >
                                        <Listbox.Options className="z-10 absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 dark:border border-gray-800 shadow-lg ring-1 ring-black/5 focus:outline-none custom-scroll-small dark:bg-darkbglight">
                                            {roles.size > 0 && Array.from(roles).map((q) => (
                                                <Listbox.Option
                                                    key={q.roleId}
                                                    title={q.roleName}
                                                    className={`cursor-pointer select-none py-2 flex items-center justify-start dark:text-gray-300 hover:bg-blue-200 dark:hover:bg-blue-600 dark:hover:text-white ${selectedRole === q.roleId ? 'bg-blue-100 text-blue-900 dark:text-white dark:bg-blue-600' : 'text-gray-900'
                                                        }`
                                                    }
                                                    value={q.roleId}
                                                >
                                                    {() => (
                                                        <>
                                                            <span className={"flex items-center px-2 text-blue-600 text-left dark:text-gray-300 " + (selectedRole !== q.roleId && 'hidden')}>
                                                                <CheckIcon className="h-3 w-3 font-bold" aria-hidden="true" />
                                                            </span>
                                                            <span className={`block truncate pr-3 text-left ${selectedRole === q.roleId ? 'font-medium' : 'font-normal pl-7'}`}>
                                                                {q.roleName}
                                                            </span>
                                                        </>
                                                    )}
                                                </Listbox.Option>
                                            ))}
                                        </Listbox.Options>
                                    </Transition>
                                </div>
                            </Listbox>
                        </div>
                    </li>
                    <li className="w-full focus-within:z-10">
                        <div className="inline-block w-full bg-white hover:text-gray-700 hover:bg-gray-50 focus:ring-4 focus:ring-blue-300 focus:outline-none dark:hover:text-white dark:bg-darkbglight/70 dark:hover:bg-darkbglight/50">
                            <Listbox value={selectedAssessment} onChange={setSelectedAssessment}>
                                <div className="relative">
                                    <Listbox.Button className={"flex items-center justify-between w-full cursor-pointer rounded-lg bg-white p-3 text-left border focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm dark:bg-transparent "+(selectedAssessment?'border-blue-600':'border-gray-300 dark:border-gray-600')}>
                                        <span className="w-full max-w-48 block font-semibold truncate dark:text-gray-300">
                                            {!roleAssessmentMap || roleAssessmentMap.length === 0 ? "No Assessment Mapped" : selectedAssessment ? Array.from(roleAssessmentMap).find((e) => selectedAssessment === e.assessmentQuestionnaireId)?.assessmentQuestionnaireName : "Assessments"}
                                        </span>
                                        <span className="pointer-events-none flex items-center pl-2">
                                            <ChevronUpDownIcon
                                                className="h-5 w-5 text-gray-400"
                                                aria-hidden="true"
                                            />
                                        </span>
                                    </Listbox.Button>
                                    <Transition
                                        as={Fragment}
                                        leave="transition ease-in duration-100"
                                        leaveFrom="opacity-100"
                                        leaveTo="opacity-0"
                                    >
                                        <Listbox.Options className="z-10 absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 dark:border border-gray-800 shadow-lg ring-1 ring-black/5 focus:outline-none custom-scroll-small dark:bg-darkbglight">
                                            {roleAssessmentMap && roleAssessmentMap
                                                .filter((q, index, self) =>
                                                    self.findIndex((item) => item.assessmentQuestionnaireId === q.assessmentQuestionnaireId) === index
                                                ).map((q) => (
                                                    (!selectedRole || q.roleId === selectedRole) && <Listbox.Option
                                                        key={q.assessmentQuestionnaireId + q.roleId}
                                                        title={q.assessmentQuestionnaireName}
                                                        className={`cursor-pointer select-none py-2 flex items-center justify-start dark:text-gray-300 hover:bg-blue-200 dark:hover:bg-blue-600 dark:hover:text-white ${selectedAssessment === q.assessmentQuestionnaireId ? 'bg-blue-100 text-blue-900 dark:text-white dark:bg-blue-600' : 'text-gray-900'
                                                            }`
                                                        }
                                                        value={q.assessmentQuestionnaireId}
                                                    >
                                                        {() => (
                                                            <>
                                                                <span className={"flex items-center px-2 text-blue-600 text-left dark:text-gray-300 " + (selectedAssessment !== q.assessmentQuestionnaireId && 'hidden')}>
                                                                    <CheckIcon className="h-3 w-3 font-bold" aria-hidden="true" />
                                                                </span>
                                                                <span className={`block truncate pr-3 text-left ${selectedAssessment === q.assessmentQuestionnaireId ? 'font-medium' : 'font-normal pl-7'}`}>
                                                                    {q.assessmentQuestionnaireName}
                                                                </span>
                                                            </>
                                                        )}
                                                    </Listbox.Option>
                                                ))}
                                        </Listbox.Options>
                                    </Transition>
                                </div>
                            </Listbox>
                        </div>
                    </li>
                    <li className="w-full focus-within:z-10">
                        <div className="inline-block w-full bg-white hover:text-gray-700 hover:bg-gray-50 focus:ring-4 focus:ring-blue-300 focus:outline-none dark:hover:text-white dark:bg-darkbglight dark:hover:bg-darkbglight/10">
                            <Listbox>
                                <div className="relative">
                                    <Listbox.Button className={"relative w-full cursor-pointer rounded-lg bg-white p-3 text-left border focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm dark:bg-transparent "+((!selectedAssessment && !selectedRole)? 'border-blue-600':'border-gray-300 dark:border-gray-600')}>
                                        <span onClick={() => { setSelectedAssessment(undefined); setSelectedRole(undefined) }} className="block font-semibold truncate dark:text-gray-300">
                                            Overall
                                        </span>
                                        <span className="pointer-events-none items-center pr-2 hidden">
                                            <ChevronUpDownIcon
                                                className="h-5 w-5 text-gray-400"
                                                aria-hidden="true"
                                            />
                                        </span>
                                    </Listbox.Button>
                                </div>
                            </Listbox>
                        </div>
                    </li>
                </ul>
                <div>
                    <div className='flex flex-col space-y-4'>
                        <div className='flex items-center justify-center'>
                            <AnalysisChart
                                QuestionDetails={filterQuestionDetails}
                                Accuracy={Question.accuracy}
                                Type={Question.type}
                            />
                        </div>
                        <div className='px-4'>
                            <p className="my-2 text-sm dark:text-white">
                                Question Group: {QuestionDetails.questionGroup?.title}
                            </p>
                            {<div className="flex-row flex items-center flex-wrap my-3" >
                                {<span className='text-sm'> Options: </span>}
                                {QuestionDetails?.answers &&
                                    QuestionDetails.answers.map((o) => {
                                        return (
                                            <span className='m-1.5 my-2'>
                                                {o.isCorrect && o.isCorrect ? (
                                                    <span
                                                        className="truncate text-gray-50 font-medium text-[11px] rounded-md decoration-gray-500 bg-green-600 py-1.5 px-3"
                                                        title={o.text}
                                                    >
                                                        {o.text.slice(0, 50) + (o.text.length > 50 ? ' ...' : '')}
                                                    </span>
                                                ) : (
                                                    <span
                                                        className="truncate text-gray-50 font-medium text-[11px] rounded-md decoration-gray-500 bg-red-600 py-1.5 px-3"
                                                        title={o.text}
                                                    >
                                                        {o.text.slice(0, 50) + (o.text.length > 50 ? ' ...' : '')}
                                                    </span>
                                                )}
                                            </span>
                                        );
                                    })
                                }
                            </div>}
                            {<div className="flex-row flex items-center flex-wrap my-3" >
                                <span className='text-sm'> Tags: </span>
                                {Question.tags.split(',').map((t) => {
                                    return (
                                        <span
                                            className="truncate text-gray-50 font-medium text-[11px] rounded-full decoration-gray-500 m-1 bg-entntblue py-1.5 px-3"
                                            title={t}
                                        >
                                            {t.slice(0, 10) + (t.length > 10 ? ' ...' : '')}
                                        </span>
                                    );
                                })}
                            </div>}
                            <div className="my-3 text-xs flex flex-row items-center gap-3">
                                <span className="dark:bg-gray-600 bg-gray-200 px-3 py-1.5 rounded-md dark:text-white">
                                    <strong>Question Type:{' '}</strong>
                                    {Question.type.toLocaleUpperCase()}
                                    {/* Single */}
                                </span>
                                <p className="dark:bg-gray-600 bg-gray-200 px-3 py-1.5 rounded-md dark:text-white">
                                    <strong>Time:</strong> {filterQuestionDetails.accuracy?.toFixed(2)} sec
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

export default QuestionAnalysis